import React from "react";
import { Check } from "lucide-react";

/**
 * Single-select option list. When `otherKey` (default "other") is selected and
 * `onOtherChange` is provided, a free-text "please specify" input appears right under it.
 */
export function OptionList({ name, options, value, onChange, labelFn, testIdPrefix, otherKey = "other", otherValue, onOtherChange, otherPlaceholder, otherError }) {
  return (
    <div role="radiogroup" aria-label={name} className="grid gap-2.5">
      {options.map((opt) => {
        const selected = value === opt;
        const showOther = selected && opt === otherKey && typeof onOtherChange === "function";
        return (
          <React.Fragment key={opt}>
            <button
              type="button"
              role="radio"
              aria-checked={selected}
              data-selected={selected}
              data-testid={`${testIdPrefix}-${opt}`}
              className="emerge-option"
              onClick={() => onChange(opt)}
            >
              <span className="emerge-radio" aria-hidden="true" />
              <span className="flex-1 leading-snug">{labelFn(opt)}</span>
              {selected && <Check className="h-5 w-5 text-primary" aria-hidden="true" />}
            </button>
            {showOther && (
              <div className="step-enter -mt-0.5 ps-8">
                <input
                  data-testid={`${testIdPrefix}-other-input`}
                  className="emerge-input"
                  value={otherValue || ""}
                  onChange={(e) => onOtherChange(e.target.value)}
                  placeholder={otherPlaceholder}
                  aria-label={otherPlaceholder}
                  aria-invalid={!!otherError}
                  maxLength={120}
                  autoFocus
                />
                {otherError && (
                  <p className="field-error" role="alert">
                    {otherError}
                  </p>
                )}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export function Field({ label, htmlFor, required, hint, error, children }) {
  return (
    <div className="py-4 first:pt-0 last:pb-0">
      <label htmlFor={htmlFor} className="mb-2 block text-[15px] font-semibold text-foreground">
        {label}
        {required && <span className="ms-1 text-destructive" aria-hidden="true">*</span>}
      </label>
      {children}
      {hint && !error && <p className="mt-1.5 text-sm text-muted-foreground">{hint}</p>}
      {error && (
        <p className="field-error" role="alert" id={htmlFor ? `${htmlFor}-error` : undefined}>
          {error}
        </p>
      )}
    </div>
  );
}

export function StepHeader({ number, title, subtitle }) {
  return (
    <div className="mb-5 flex items-start gap-3">
      <span className="step-num mt-1">{String(number).padStart(2, "0")}</span>
      <div>
        <h1 className="text-xl font-semibold leading-snug text-foreground sm:text-2xl">{title}</h1>
        {subtitle && <p className="mt-1.5 text-[15px] text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
  );
}
